import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { AvailableSSI, Bank, defaultSalesInvoicesState, SalesInvoicesStateModel } from './sales-invoices.model';
import { SalesInvoicesService } from '../sales-invoices.service';
import { catchError, tap } from 'rxjs/operators';
import { GetAvailableSSIs, GetSalesInvoices, OnGetSalesProductComponents, OnSalesShippingInstructionsSelected, OnSaveForm, OnSetAllBanks, OnSetIsNew, SetSalesInvoicesFormGridConfig, OnSavePaymentForm, PaymentComponentsGridConfig, OnSetCurrentSSIRenderer, SetCurrentSalesInvoice, DeleteSalesInvoice, ExportSalesInvoices, DeleteSalesInvoicePayment } from './sales-invoices.actions';
import { OnRemoveSharedGridRowData, OnSetSharedGridConfig, OnSetSharedGridRowData, SetGridDates } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions';
import { SharedGridState } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.state';
import { produce } from 'immer';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { OnCanCloseDialog, SetShowLoader, ShowToastrAlert } from 'app/modules/shared-state/shared.actions';
import { resolveExportHttpErrorMessage } from 'app/modules/shared/http-error-message.util';
import { of } from 'rxjs';

@State<SalesInvoicesStateModel>({
  name: 'salesInvoices',
  defaults: defaultSalesInvoicesState,
})

@Injectable()
export class SalesInvoiceState {
  private _store = inject(Store);
  private _salesInvoicesService = inject(SalesInvoicesService);

  @Action(GetSalesInvoices)
  getSalesInvoices(ctx: StateContext<SalesInvoicesStateModel>) {

    const gridDates = this._store.selectSnapshot(SharedGridState.getGridDates);

    ctx.dispatch(new OnSetSharedGridRowData({
      reload: true,
      payload: [],
      gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
    }));
    ctx.dispatch(new SetShowLoader(true));

    return this._salesInvoicesService.getAllInvoices(gridDates.startDate, gridDates.endDate).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: any) => {
        ctx.dispatch(new OnSetSharedGridRowData({
          reload: true,
          payload: data,
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
        }));
        ctx.dispatch(new SetGridDates({
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID,
          startDate: gridDates.startDate,
          endDate: gridDates.endDate,
          displayStartDate: gridDates.startDate,
          displayEndDate: gridDates.endDate
        }));
        ctx.dispatch(new SetShowLoader(false));
      })
    );
  }

  @Action(SetSalesInvoicesFormGridConfig)
  setSalesConfirmationsFormGridConfig(ctx: StateContext<SalesInvoicesStateModel>, { payload }: SetSalesInvoicesFormGridConfig) {

    const store = inject(Store);
    const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);

    const updatedGridConfig = produce(parentGridConfig, (draft) => {

      draft[payload.gridUUID] = payload.gridConfig[payload.gridUUID];
    });
    ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));

  }
  @Action(OnGetSalesProductComponents)
  async onGetSalesProductComponents(ctx: StateContext<SalesInvoicesStateModel>, { _id }: OnGetSalesProductComponents) {
    return this._salesInvoicesService.getSalesProductComponents(_id).pipe(
      catchError((error) => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((response: any) => {
        ctx.dispatch(new OnSetSharedGridRowData({
          reload: true, payload: response, gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
        }));
      }),
    );
  }

  @Action(GetAvailableSSIs)
  async GetAvailableSSIs(ctx: StateContext<SalesInvoicesStateModel>) {
    return this._salesInvoicesService.getAvailableSSIs().pipe(
      tap((ssi: AvailableSSI[]) => {
        ctx.patchState({ availableSSIs: ssi });
      }));
  }

  @Selector()
  static getAvailableSSIs(state: SalesInvoicesStateModel) {
    return state.availableSSIs;
  }

  @Action(OnSalesShippingInstructionsSelected)
  async onSalesShippingInstructionsSelected(ctx: StateContext<SalesInvoicesStateModel>, { _id }: OnSalesShippingInstructionsSelected) {
    return this._salesInvoicesService.getFormData(_id).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: any) => {
        ctx.dispatch(new UpdateFormValue({
          path: 'salesInvoices.salesInvoicesForm',
          value: {
            invoiceFor: data.invoiceFor,
            deliveryTerms: data.deliveryTerms,
            paymentTerms: data.paymentTerms,
            customerName: data.customerName,
            customerId: data.customerId,
            customerAddress: data.customerAddress,
          }
        }));
        ctx.dispatch(new SetShowLoader(false));
      }));
  }

  @Action(OnSetAllBanks)
  async onSetAllBanks(ctx: StateContext<SalesInvoicesStateModel>) {
    return this._salesInvoicesService.getBanks().pipe(
      tap((result: Bank[]) => {
        ctx.patchState({ banks: result });
      })
    );
  }
  @Selector()
  static getBanks(state: SalesInvoicesStateModel) {
    return state.banks;
  }
  @Action(OnSaveForm)
  async onSaveForm(ctx: StateContext<SalesInvoicesStateModel>) {
    if (ctx.getState().isNew) {
      const form = ctx.getState().salesInvoicesForm.model;
      const payload = {
        invoiceNumber: form.invoiceNumber.toUpperCase(),
        invoiceFor: form.invoiceFor,
        customerAddress: form.customerAddress,
        paymentTerms: form.paymentTerms,
        deliveryTerms: form.deliveryTerms,
        ssiReference: form.ssiReference?.ssiReference,
        salesInvoiceDate: new Date(form.salesInvoiceDate).toISOString(),
        expectedPaymentDate: new Date(form.expectedPaymentDate).toISOString(),
        customerId: form.customerId,
        bankId: form.bankId,
        salesShippingInstructionId: form.ssiReference?._id
      };
      return this._salesInvoicesService.createSalesInvoice(payload).pipe(
        catchError((error) => {
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: error.error.message.message,
            position: 'toast-top-center'
          }));
          throw error;
        }),
        tap(() => {
          ctx.dispatch(new GetSalesInvoices());
          ctx.dispatch(new SetShowLoader(false));
        })
      );
    } else {
      const form = ctx.getState().salesInvoicesForm.model;
      const payload = {
        invoiceNumber: form.invoiceNumber.toUpperCase(),
        invoiceFor: form.invoiceFor,
        customerAddress: form.customerAddress,
        paymentTerms: form.paymentTerms,
        deliveryTerms: form.deliveryTerms,
        salesInvoiceDate: new Date(form.salesInvoiceDate).toISOString(),
        expectedPaymentDate: new Date(form.expectedPaymentDate).toISOString(),
        bankId: form.bankId,
      };

      return this._salesInvoicesService.updateSalesInvoice(ctx.getState().currentSalesInvoice, ctx.getState().currentChecksum, payload).pipe(
        catchError((error) => {
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: error.error.message.message,
            position: 'toast-top-center'
          }));
          throw error;
        }),
        tap(() => {
          ctx.dispatch(new GetSalesInvoices());
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'success',
            messageTitle: 'Success',
            messageBody: 'Payment created successfully',
            position: 'toast-top-center'
          }));
        })
      );
    };
  }

  @Action(OnSetIsNew)
  async onSetIsNew(ctx: StateContext<SalesInvoicesStateModel>, { isNew }: OnSetIsNew) {
    ctx.patchState({ isNew: isNew });
  }
  @Selector()
  static getIsNew(state: SalesInvoicesStateModel) {
    return state.isNew;
  }

  @Action(OnSavePaymentForm)
  async onSavePaymentForm(ctx: StateContext<SalesInvoicesStateModel>, { payload }: OnSavePaymentForm) {
    ctx.dispatch(new SetShowLoader(true));
    const form = ctx.getState().paymentForm.model;
    const apiPayload = {
      paymentAmount: form.paymentAmount,
      partialPaymentDate: new Date(form.partialPaymentDate).toISOString(),
      paymentRef: form.paymentRef,
    };
    return this._salesInvoicesService.createPayment(apiPayload, payload.invoiceId).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap(() => {
        this._store.dispatch(new OnCanCloseDialog(true));
        ctx.dispatch(new GetSalesInvoices());
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: 'Payment created successfully',
          position: 'toast-top-center'
        }));
      })
    );
  }

  @Action(DeleteSalesInvoice)
  deleteSalesInvoice(ctx: StateContext<SalesInvoicesStateModel>, { _id }: DeleteSalesInvoice) {
    ctx.dispatch(new SetShowLoader(true));
    return this._salesInvoicesService.deleteSalesInvoice(_id).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap(() => {
        this._store.dispatch(new OnCanCloseDialog(true));
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new OnRemoveSharedGridRowData({
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID,
          removeRow: {
            _id: _id
          }
        }));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: 'Sales Invoice Deleted Succesfully',
          position: 'toast-top-center'
        }));
      })
    );
  }

  //Cel renderer
  @Action(PaymentComponentsGridConfig)
  setSalesShippingComponentsGridConfig(ctx: StateContext<SalesInvoicesStateModel>, { payload }: PaymentComponentsGridConfig) {

    const store = inject(Store);
    const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);

    const updatedGridConfig = produce(parentGridConfig, (draft) => {

      draft[payload.gridUUID] = payload.gridConfig[payload.gridUUID];
    });
    ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));

  }

  @Action(OnSetCurrentSSIRenderer)
  onSetCurrentRenderer(ctx: StateContext<SalesInvoicesStateModel>, { payload }: OnSetCurrentSSIRenderer) {
    ctx.patchState({
      currentRendererId: payload.currentRenderer
    });
  }


  @Selector()
  static getCurrentRendererId(state: SalesInvoicesStateModel) {
    return state.currentRendererId;
  }

  @Action(SetCurrentSalesInvoice)
  setCurrentSalesInvoice(ctx: StateContext<SalesInvoicesStateModel>, { payload }: SetCurrentSalesInvoice) {
    ctx.patchState({
      currentSalesInvoice: payload._id,
      currentChecksum: payload.md5Checksum,
    });
  }

  @Action(ExportSalesInvoices)
  exportSalesInvoices(ctx: StateContext<SalesInvoicesStateModel>) {
    ctx.dispatch(new SetShowLoader(true));

    const gridDates = this._store.selectSnapshot(SharedGridState.getGridDates);

    return this._salesInvoicesService.exportSalesInvoices(gridDates.startDate, gridDates.endDate).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        const messageBody = resolveExportHttpErrorMessage(error, 'Could not export sales invoices.');
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((response: HttpResponse<Blob>) => {
        const blob = response.body;
        if (!blob) {
          ctx.dispatch(new SetShowLoader(false));
          return;
        }
        const header = response.headers.get('Content-Disposition');
        let filename = `sales-invoices-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
        if (header) {
          const match = /filename="([^"]+)"/.exec(header) ?? /filename=([^;\s]+)/.exec(header);
          if (match) {
            filename = match[1];
          }
        }
        saveAs(blob, filename);
        ctx.dispatch(new SetShowLoader(false));
      })
    );
  }

  @Action(DeleteSalesInvoicePayment)
  deleteSalesInvoicePayment(ctx: StateContext<SalesInvoicesStateModel>, { invoiceId, payment }: DeleteSalesInvoicePayment) {
    ctx.dispatch(new SetShowLoader(true));

    return this._salesInvoicesService.deletePayment(invoiceId, payment).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));

        if (error.status === 404) {
          ctx.dispatch(new GetSalesInvoices());
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'warning',
            messageTitle: 'Payment not found',
            messageBody: 'This payment no longer exists. The list has been refreshed.',
            position: 'toast-top-center',
          }));
          return of(null);
        }

        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: 'An error occurred while trying to delete the payment. Please try again.',
          position: 'toast-top-center',
        }));
        throw error;
      }),
      tap((result) => {
        if (result === null) return;

        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new GetSalesInvoices());
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: 'Payment deleted successfully',
          position: 'toast-top-center',
        }));
      }),
    );
  }

  // @Action(GetSalesShippingComponentsByParentId)
  // getSalesShippingComponentsByParentId(ctx: StateContext<SalesInvoicesStateModel>, { payload }: GetSalesShippingComponentsByParentId) {
  //   return this._salesShippingInstructionsService.getSalesShippingComponentsByParentId(payload.parentId).pipe(
  //     tap((response: any) => {
  //       ctx.dispatch(new OnSetSharedGridRowData({
  //         reload: true,
  //         payload: response,
  //         gridUUID: payload.gridUUID
  //       }));
  //     })
  //   );
  // }
}